import {
  Note,
  SessionResponse,
  DeviceEnum,
  PageTypeEnum,
  PageLifecycleState,
  PlatformEnum,
  PageDesc,
  SessionId,
  ClientId,
} from '@fullstory/client-api';
import type { S } from '@fullstory/util/sexpr';
import { AppData, DeviceData } from '@fullstory/client-api/goimports/fs_shared_schema';
import { NormalizedPageDesc, PageModel } from './Page';
import { LinearizedTabModel, TabModel } from './Tab';
import { PageFetcherContext, SessionFetcher } from '../fetchers';
import { sortBy } from 'lodash';
import { PageList } from './PageList';
import { from, map, merge, mergeMap, Observable, share, skip } from 'rxjs';
import { AppendOnlyArray } from '@fullstory/util/collections';
import { RawEvent } from '@fullstory/recording-api';
import { PageDescType, pagePlatformByType } from '../pagetypes';
import { currentPageAtTime } from '../utils/currentPageAtTime';

export type NormalizedSessionResponse = SessionResponse & {
  Pages: NormalizedPageDesc[];
};

type PageSearchOptions = { isExact: true } | { isExact: false; currentTimeMs?: number };

type ChangeTab = {
  absTime: number;
  type: 'change_tab';
  fromPageId: string;
  fromTabId: string;
  toPageId: string;
  toTabId: string;
};

type CloseTab = {
  absTime: number;
  type: 'close_tab';
  pageId: string;
  tabId: string;
};

type NewTabTransition = {
  absTime: number;
  type: 'new_tab';
  pageId: string;
  tabId: string;
};

type Transition = ChangeTab | CloseTab | NewTabTransition;
// A collection of pages representing either a session (in the normal case) or a
// clip.
// TODO(sethf): Clips may contain multiple pages from unrelated sessions with
// different users and timelines, which breaks many assumptions that seem
// natural for code that expects to deal only with sessions. We should clean
// this up somehow, either by creating a separate ClipModel for clips, or by not
// representing clips at this level at all and orchestrating them at a higher
// level in the app code.
// TODO(jordanhurt): Refactor the various accessors and methods in this class to
// reference the tab timeline by default.

export class SessionModel {
  static async fromSExpr(args: {
    ctx: PageFetcherContext;
    sexpr: S[];
    prefetchPages: boolean;
  }): Promise<SessionModel> {
    const { ctx, prefetchPages = false, sexpr } = args;

    // Initialize the fetcher and session response
    const sessionFetcher = new SessionFetcher(ctx);
    const sessionResponse = await sessionFetcher.fetchSessions(sexpr);
    const session = new SessionModel(ctx, sessionResponse, sessionFetcher);

    if (prefetchPages) {
      await Promise.all(session.pages.map(page => page.getCookedPage()));
    }

    // Return the session model
    return session;
  }

  private readonly _session: Readonly<NormalizedSessionResponse>;

  private readonly _linearized: LinearizedTabModel;

  private readonly _pages = new PageList();

  private readonly _pageUpdates$: Observable<PageModel>;

  private readonly _tabs = new Map<string, TabModel>();

  private _transitions: Transition[] = [];

  private _normalizePageDescs: (descs: PageDesc[]) => NormalizedPageDesc[];

  /**
   * Add new page descs. NOTE: pages should _only_ be added via
   * this method.
   */
  private _addPageDescs(pageDescs: NormalizedPageDesc[]) {
    const batch: PageModel[] = [];

    for (const desc of pageDescs) {
      const page = new PageModel(this._ctx, desc);

      batch.push(page);

      // Tabs are a browser-only concept, so ignore them for non-web platfoms
      if (pagePlatformByType[page.type] === 'web') {
        this._getOrCreateTab(page.tabId).addPages([page]);
      }
    }

    this._linearized.addPages(batch);
    this._pages.append(batch);
  }

  private _getOrCreateTab(tabId: string): TabModel {
    if (!this._tabs.has(tabId)) {
      this._tabs.set(tabId, new TabModel(this._ctx, tabId, []));
    }

    // TypeScript isn't smart enough to know Tab is defined so the ! operator is okay
    return this._tabs.get(tabId)!;
  }

  private _calculateTransitions() {
    this._transitions = [];

    const tabs = this.tabs;

    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i]!;
      const firstPage = tab.page(0);

      // We ignore the first new_tab event because it is implied and showing it
      // would be confusing to the user.
      if (firstPage && i > 0) {
        this._transitions.push({
          absTime: firstPage.localStartTime,
          type: 'new_tab',
          pageId: firstPage.pageId,
          tabId: tab.tabId,
        });
      }

      const lastPage = tab.lastPage();
      // We should only infer that a tab was closed if it ended before the session
      // The final tab close is implied, and sometimes all tabs are closed at once.
      // NOTE: the page is considered ended the moment _after_ start + duration
      if (
        lastPage &&
        lastPage.closed &&
        lastPage.localStartTime + lastPage.duration + 1 < this.endTime
      ) {
        this._transitions.push({
          // The page is considered closed the moment _after_ start + duration
          absTime: lastPage.localStartTime + lastPage.duration + 1,
          type: 'close_tab',
          pageId: lastPage.pageId,
          tabId: tab.tabId,
        });
      }
    }

    // Calculate the possible times where a tab_change event might have occurred.
    // We use a simple, but reliable heuristic -- tab changes generally correspond
    // to the start of a page, end of a page, or lifecycle transition (e.g. active -> passive)
    // within the page.
    const possibleTabChanges = new Set<number>();

    for (const page of this.pages) {
      // Do not add possible tab changes for non-web pages
      if (pagePlatformByType[page.type] !== 'web') continue;

      const { localStartTime, duration } = page;
      const endTime = localStartTime + duration + 1;

      possibleTabChanges.add(localStartTime);

      for (const { start } of page.lifecycleStates) {
        possibleTabChanges.add(start);
      }

      possibleTabChanges.add(endTime);
    }

    let prevPage: PageModel | undefined = undefined;

    for (const absTime of Array.from(possibleTabChanges).sort((a, b) => a - b)) {
      const page = this.currentPageAtTime(absTime);

      if (prevPage && page && prevPage !== page && prevPage?.tabId !== page?.tabId) {
        this.transitions.push({
          absTime,
          type: 'change_tab',
          fromPageId: prevPage.pageId,
          fromTabId: prevPage.tabId,
          toPageId: page.pageId,
          toTabId: page.tabId,
        });
      }

      prevPage = page;
    }

    // Sort the transitions by absolute time
    this.transitions.sort((a, b) => a.absTime - b.absTime);
  }

  constructor(
    private _ctx: PageFetcherContext,
    session: SessionResponse,
    private _fetcher = new SessionFetcher(_ctx),
  ) {
    this._normalizePageDescs = createPageNormalizer();
    this._linearized = new LinearizedTabModel(this._ctx, 'linearized', []);
    this._session = {
      ...session,
      Pages: this._normalizePageDescs(session.Pages),
    } as NormalizedSessionResponse;

    // Add pages, calculate our initial transitions, and subscribe to updates
    this._addPageDescs(this._session.Pages);
    this._calculateTransitions();

    // Re-calculate transitions whenever a page is added or modified
    this._pageUpdates$ = toPageUpdates$(this._pages);
    this._pageUpdates$.subscribe(() => {
      this._calculateTransitions();
    });
  }

  get sessionIds(): SessionId[] {
    return this._session.SessionIds;
  }

  get clientIds(): ClientId[] {
    return this._session.ClientIds;
  }

  // Gets the session as a single tab model that's been linearized (read: faked) so that its pages are non-overlapping.
  get linearized(): LinearizedTabModel {
    return this._linearized;
  }

  /**
   * Pages sorted by *non-linearized* start time.
   */
  get pages(): PageModel[] {
    return sortBy([...this._pages] as PageModel[], 'localStartTime');
  }

  get pageList(): AppendOnlyArray<PageModel> {
    return this._pages;
  }

  /**
   * Returns an Observable that emits whenever a page is added or updated
   * NOTE: the initial pages provided to the session are not emitted
   */
  get pageUpdates$(): Observable<PageModel> {
    return this._pageUpdates$;
  }

  /**
   * Tabs sorted by *non-linearized* start time
   */
  get tabs(): TabModel[] {
    return sortBy([...this._tabs.values()], 'startTime');
  }

  get country(): string {
    return this._session.Country;
  }

  get startTime(): number {
    return this._pages.startTime;
  }

  get endTime(): number {
    return this._pages.endTime;
  }

  get region(): string {
    return this._session.Region;
  }

  get city(): string {
    return this._session.City;
  }

  get ip(): string {
    return this._session.IP;
  }

  get latLong(): string {
    return this._session.LatLong;
  }

  get lastPage(): PageModel | undefined {
    return this._linearized.lastPage();
  }

  get userAgent(): string {
    return this._session.RawUserAgent;
  }

  get browser(): string {
    return this._session.UABrowser;
  }

  get os(): string {
    return this._session.UAOperatingSystem;
  }

  get deviceModel(): string {
    return this._session.DeviceModel;
  }

  get deviceData(): DeviceData | undefined {
    // device data is the same across all pages, but can sometimes be left unset due to incomplete cook artifacts.
    // Return the first set instance that we find.
    for (const page of this._linearized.pages) {
      const deviceData = page.getRawPageDesc().DeviceData;
      if (deviceData) {
        return deviceData;
      }
    }
    return undefined;
  }

  get resolution(): string | undefined {
    const lastPage: PageModel | undefined = this._linearized.lastPage();
    // don't return if we don't have a width/height
    if (!lastPage || !lastPage.screenWidth || !lastPage.screenHeight) {
      return undefined;
    }
    return `${lastPage.screenWidth}X${lastPage.screenHeight}`;
  }

  get displayName(): string {
    return this._session.UserDisplayName;
  }

  get created(): number {
    return this._session.Created;
  }

  get email(): string {
    return this._session.UserEmail;
  }

  /**
   * @deprecated
   * This field is inaccurate because it is based on the first page's platform,
   * but a session can contain pages of multiple patforms, like Web and Server.
   */
  get device(): DeviceEnum {
    const platform = this.platform;
    if (platform === PlatformEnum.PlatformNativeMobile) {
      return DeviceEnum.DeviceMobile;
    }
    if (this._session.UADevice) {
      return this._session.UADevice as DeviceEnum;
    }
    return DeviceEnum.DeviceUnknown;
  }

  get appData(): AppData | undefined {
    // app data is the same across all pages, but can sometimes be left unset due to incomplete cook artifacts.
    // Return the first set instance that we find.
    for (const page of this._linearized.pages) {
      const appData = page.getRawPageDesc().AppData;
      if (appData) {
        return appData;
      }
    }
    return undefined;
  }

  /**
   * @deprecated
   * This field is unreliable because a session can contain pages of multiple platforms.
   */
  get platform(): PlatformEnum {
    const firstPage = this.linearized.page(0);
    if (!firstPage) {
      return PlatformEnum.PlatformWeb;
    }
    switch (firstPage.type) {
      case PageTypeEnum.PageTypeAndroid:
      case PageTypeEnum.PageTypeiOS:
      case PageTypeEnum.PageTypeMobileMock:
        return PlatformEnum.PlatformNativeMobile;
      case PageTypeEnum.PageTypeServer:
        return PlatformEnum.PlatformServer;
      default:
        return PlatformEnum.PlatformWeb;
    }
  }

  /**
   * When the playback session is embeded in another app beside the FSTA we
   * do not have a concept of an orgId within the app context. So this function
   * derives the orgId from the active session that is being played.
   *
   * We do this my parsing the session's cookedPageUrl for the orgId
   *
   * TODO: Come up with a cleaner way to get the org id from the server.
   * We should also try to simplify the role of the "testdrive".
   * of the "testdrive" id.
   */
  get dangerouslyInferredOrgId(): string | null {
    // sanity check
    if (!this._session || !this._session.Pages || this._session.Pages.length <= 0) {
      return null;
    }

    for (const page of this._session.Pages) {
      if (page.CookedPageUrls && page.CookedPageUrls.length > 0) {
        for (const cookedPagesUrls of page.CookedPageUrls) {
          const searchCookedPageParams = new URL(cookedPagesUrls, 'https://www.fullstory.com')
            .searchParams;
          const possibleOrgId = searchCookedPageParams.get('OrgId');
          if (possibleOrgId) {
            return possibleOrgId;
          }
        }
      }
    }

    return null;
  }

  get sentIntegrations(): Readonly<{ [id: string]: string }> {
    return this._session.SentIntegrations;
  }

  get transitions(): Transition[] {
    return this._transitions;
  }

  addNote(note: Note): void {
    // Find the right spot in the SessionResponse object, drop in the note, and re-render.
    const page = this.pageById(note.PageId);
    if (page) {
      page.addNote(note);
    }
  }

  /**
   * Returns an ordered list of all pages that existed at a point in time
   */
  allPagesAtTime(absTime: number, opts: PageSearchOptions = { isExact: true }): PageModel[] {
    const pages: PageModel[] = [];

    if (opts.isExact) {
      // get all pages that existed at the specified time
      for (const page of this._pages.at(absTime)) {
        if (!page) {
          // sanity check
          continue;
        }
        const lifecycleState = page.lifecycleStateAt(absTime)?.value;

        if (lifecycleState !== PageLifecycleState.TERMINATED) {
          pages.push(page as PageModel);
        }
      }
    } else {
      // non-strict is used to get all pages that existed at the specified time or within the last 30 minutes
      const { currentTimeMs = new Date().getTime() } = opts;
      for (const page of this._pages.atOrBefore(absTime)) {
        if (!page) {
          // sanity check
          continue;
        }
        const lifecycleState = page.lifecycleStateAt(absTime)?.value;
        const thirtyMinutesInMs = 30 * 60 * 1000; // 30 minutes * 60 seconds * 1000 milliseconds === 30 minutes in milliseconds
        const thirtyMinutesAgoMs = new Date(currentTimeMs - thirtyMinutesInMs).getTime();

        if (
          lifecycleState !== PageLifecycleState.TERMINATED &&
          // if endTime ms timestamp is greater the 30 minutes ago ms timestamp then an event was recorded within the last 30 minutes
          page.localEndTime > thirtyMinutesAgoMs
        ) {
          pages.push(page as PageModel);
        }
      }
    }

    return pages;
  }

  /**
   * Returns an ordered list of all tabs that existed at a point in time
   */
  allTabsAtTime(absTime: number, opts: PageSearchOptions = { isExact: true }): TabModel[] {
    // NOTE: Set is used because it prevents duplicates
    // [MDN: Set](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set)
    const tabs = new Set<TabModel>();
    if (opts.isExact) {
      for (const tab of this.tabs) {
        if (absTime >= tab.startTime && absTime <= tab.endTime) {
          tabs.add(tab);
        }
      }
    } else {
      const pages = this.allPagesAtTime(absTime, opts);
      for (const page of pages) {
        const tab = this.tabById(page.tabId);
        if (tab) {
          tabs.add(tab);
        }
      }
    }

    return sortBy([...tabs], 'startTime');
  }

  /**
   * Look up the current page at a point in time. The current page
   * is the page that the end user was most recently viewing.
   * NOTE: The current page is NOT guaranteed to be active. In many cases, it may be passive or hidden.
   * [Page Lifecycle](https://developer.chrome.com/blog/page-lifecycle-api/#overview-of-page-lifecycle-states-and-events)
   */
  currentPageAtTime(
    absTime: number,
    opts: PageSearchOptions = { isExact: true },
  ): PageModel | undefined {
    const pages = this.allPagesAtTime(absTime, opts);
    return currentPageAtTime(pages, absTime);
  }

  /**
   * Returns the active tab from the window that was focused (or most recently focused,
   * if no browser windows were focused at the time). This can be thought of as the
   * user's current tab.
   * Note: Always returns undefined for non-web sessions
   */
  currentTabAtTime(
    absTime: number,
    opts: PageSearchOptions = { isExact: true },
  ): TabModel | undefined {
    const currentPage = this.currentPageAtTime(absTime, opts);

    // Tabs are a web-specific concept, so non-web pages should not have corresponding tabs
    if (pagePlatformByType[currentPage?.type ?? ''] !== 'web') return undefined;

    return this._tabs.get(currentPage?.tabId ?? '');
  }

  /**
   * Lookup a tab by it's id in O(1) time
   */
  tabById(tabId: string): TabModel | undefined {
    return this._tabs.get(tabId);
  }

  tabByPageId(pageId: string): TabModel | undefined {
    return this.tabById(this.pageById(pageId)?.tabId ?? '');
  }

  pageById(pageId: string): PageModel | undefined {
    return this._pages.byId(pageId) as PageModel | undefined;
  }

  /**
   * Finds a page by its unique key generated via {@link PageModel.getKey}
   */
  pageByKey(key: string): PageModel | undefined {
    return this._pages.byKey(key) as PageModel | undefined;
  }

  private async _checkForTrailingPages(currentTime: number) {
    // Check for trailing events from the current page
    const currentPage = this.currentPageAtTime(currentTime);

    // Sanity check
    if (!currentPage) {
      return [];
    }

    // Fetch any trailing pages
    const trailingPagesResponse = await this._fetcher.fetchTrailingPages(currentPage);
    const trailingPages = trailingPagesResponse?.TrailingPages ?? [];

    // Finally, update state based on the latest information.
    this._addPageDescs(this._normalizePageDescs(trailingPages));

    return trailingPages;
  }

  /**
   * Checks for trailing pages and trailing events and returns a boolean
   * indicating if trailing data was returned.
   */
  async checkForTrailingData(
    currentTime: number,
  ): Promise<{ trailingPages: PageDesc[]; trailingEvents: RawEvent[] }> {
    // Fetch any trailing pages before doing anything else to ensure
    // we subsequently fetch trailing events from the latest pages
    const trailingPages = await this._checkForTrailingPages(currentTime);

    // Additionally, check for trailing events for any page that isn't currently closed
    const allPages = this.pages.filter(page => !page.closed);
    const allPagesTrailing = await Promise.all(allPages.map(page => page.checkForTrailingEvents()));

    // Combine everything into a single trailing events list
    const trailingEvents = allPagesTrailing.flat();

    return { trailingEvents, trailingPages };
  }

  /**
   * @returns the raw response to the /session? endpoint. Use this only for
   * debugging purposes. Production code should use the accessors on this class.
   */
  get rawSessionResponse(): Readonly<SessionResponse> {
    return this._session;
  }

  destroy() {
    for (const page of this._pages) {
      page.destroy();
    }
    this._pages.close();

    for (const tab of this._tabs.values()) {
      this._tabs.delete(tab.tabId);
      tab.destroy();
    }
  }
}

/**
 * Converts an {@link AppendOnlyArray} of {@link PageModel} into an Observable that emits
 * whenever a page is added or updated. The Observable will only close once the page
 * list is closed _and_ all included pages are closed to future updates.
 *
 * TODO(jordanhurt) - investigate if using batches improves performance. e.g.
 * ```
 * const added$ = from(pages.batches());
 * const updated$ = from(pages[Symbol.asyncIterator]()).pipe(
 *   mergeMap(page =>
 *     page.duration$.pipe(
 *       skip(1),
 *       map(() => [page as PageModel]),
 *     )
 *   )
 * )
 * ```
 * Note: It's not clear that batching would yield a real-world peformance benefit
 * because very few pages are added during Go Live mode, and the duration changes
 * can't easily be batched.
 */
export function toPageUpdates$(pages: AppendOnlyArray<PageModel>): Observable<PageModel> {
  const added$ = from(pages[Symbol.asyncIterator]()).pipe(skip(pages.length), share());
  const updated$ = from(pages[Symbol.asyncIterator]()).pipe(
    mergeMap(page =>
      page.duration$.pipe(
        skip(1),
        map(() => page),
      ),
    ),
    share(),
  );
  return merge(added$, updated$).pipe(map(page => page as PageModel));
}

type PageNormalizer = (pages: PageDesc[]) => NormalizedPageDesc[];

/**
 * Normalizes batches of PageDescs to ensure necessary properties, like TabId, are defined.
 */
function createPageNormalizer(): PageNormalizer {
  // When pages are missing a `TabId` property, we have to generate one.
  // When no `TabId` was recorded, our heuristic is to assume that overlapping
  // pages were viewed in separate tabs, and adjacent pages (within a 200ms tolerance)
  // were viewed in the same time. To ensure stable IDs for a given session, we use an
  // incrementing integer-based ID.
  //
  // The `pageEndTimes` array stores previously encountered page end times at an index that
  // was used for that page's tab Id.
  // Then, the `allocateTabId` function searches for an end time that is within the tolerance
  // of the _new_ page's start time (to determine if the new page is adjacent).
  // When a match is found, the end time at that index is updated to the new page's start time.
  //
  // Note: Closure is needed to ensure tab Ids are not duplicated in Go Live mode when we receive
  // batches over time.
  const pageEndTimes: number[] = [];
  const allocateTabId = (startTime: number, endTime: number) => {
    // We use tolerance as a heuristic to determine when the next page's
    // start time is _close enough_ to the previous page's end time.
    // We must allow some tolerance because start/end timing could
    // be slightly off for pages within the same tab due to
    // async code execution, sendBeacon API timing, etc.
    const toleranceMs = 200;

    for (let i = 0; i < pageEndTimes.length; i++) {
      const time = pageEndTimes[i]!;

      if (Math.abs(startTime - time) < toleranceMs) {
        // update this ID to a new end time
        pageEndTimes[i] = endTime;
        const id = `${i}`;
        return id;
      }
    }

    // Create a new entry because we have an unusable time
    pageEndTimes.push(endTime);
    return `${pageEndTimes.length - 1}`;
  };

  return (pages: PageDesc[]): NormalizedPageDesc[] => {
    if (!Array.isArray(pages)) {
      return [];
    }

    const normalized: NormalizedPageDesc[] = [];

    // Clean up and normalize the data structures in SessionResponse.
    for (const desc of pages) {
      // Normalize RawEventsUrls to an array. The server won't populate the
      // field if it's empty.
      if (!desc.RawEventsUrls) {
        desc.RawEventsUrls = [];
      }

      // TabId can be undefined when the `SessionStorage` API was unavailable,
      // so we should generate a tab ID for web pages that are missing one.
      if (pagePlatformByType[desc.Type as PageDescType] === 'web' && !desc.TabId) {
        // TODO(jordanhurt) - We currently assume each page was in a unique tab
        // when tabId is undefined, but we should improve this fallback case to
        // assume consecutive pages were viewed in the same tab.
        const { StartTime: startTime, Duration: duration = 0 } = desc;
        desc.TabId = allocateTabId(startTime, startTime + duration);
      }

      normalized.push(desc as NormalizedPageDesc);
    }

    return normalized;
  };
}
